<script setup>
// How the recipient list gets loaded. Cards rather than tabs because this is the fork in
// the road of the whole flow — the operator picks it once and everything below follows
// from it. Mirrors the Workforce screen the operators already know.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: 'hubspot' },
  hubspotAvailable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Labels resolved with literal keys so the i18n linter can verify every one of them.
const sources = computed(() => [
  {
    value: 'hubspot',
    icon: 'i-lucide-link',
    label: t('OUTBOUNDS.SOURCE.HUBSPOT'),
    hint: t('OUTBOUNDS.SOURCE.HUBSPOT_HINT'),
    disabled: props.disabled || !props.hubspotAvailable,
  },
  {
    value: 'label',
    icon: 'i-lucide-tag',
    label: t('OUTBOUNDS.SOURCE.LABEL'),
    hint: t('OUTBOUNDS.SOURCE.LABEL_HINT'),
    disabled: props.disabled,
  },
  {
    value: 'manual',
    icon: 'i-lucide-square-pen',
    label: t('OUTBOUNDS.SOURCE.MANUAL'),
    hint: t('OUTBOUNDS.SOURCE.MANUAL_HINT'),
    disabled: props.disabled,
  },
  {
    value: 'csv',
    icon: 'i-lucide-upload-cloud',
    label: t('OUTBOUNDS.SOURCE.CSV'),
    hint: t('OUTBOUNDS.SOURCE.CSV_HINT_SHORT'),
    disabled: props.disabled,
  },
]);
</script>

<template>
  <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    <button
      v-for="source in sources"
      :key="source.value"
      type="button"
      :disabled="source.disabled"
      :aria-pressed="modelValue === source.value"
      class="flex flex-col items-center justify-center gap-2 px-5 py-6 text-sm font-semibold rounded-xl border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      :class="
        modelValue === source.value
          ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
          : 'border-n-weak bg-n-background text-n-slate-12 hover:border-n-slate-5'
      "
      @click="emit('update:modelValue', source.value)"
    >
      <i :class="`${source.icon} size-6 text-n-brand`" />
      <span>{{ source.label }}</span>
      <span class="text-xs font-normal text-n-slate-10">{{ source.hint }}</span>
    </button>
  </div>
</template>
